import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { EndpointHint, type AdminClient } from '../../../modules/core/index.js'
import { createExecutiveInsightsAdminApi } from '../adminApi.js'
import { formatHeadline, monthLabel } from '../labels.js'

/**
 * "Insights: 11 facilities counted through September 2026 · average score 82.1" on the admin's client detail: the
 * add-on's read-only mirror, with where the license stands, so the admin can see whether the client's Insights page
 * has anything on it. Nothing to edit here.
 */
export function AdminClientInsightsLine({ client, clientId }: { client: AdminClient; clientId: string }) {
  const api = useMemo(() => createExecutiveInsightsAdminApi(client), [client])
  const query = useQuery({ queryKey: ['admin', 'client', clientId, 'insights'], queryFn: () => api.insightsPortfolio(clientId, 2) })
  if (query.isPending) {
    return <p className="text-xs text-ink-mid">Insights: counting…</p>
  }
  if (query.isError) {
    return <p className="text-xs text-ink-mid">Insights: not available on this API.</p>
  }
  const { data, feature } = query.data
  const counted = data.facilities.filter((row) => row.latest !== null).length
  const latest = data.totals.latest
  return (
    <p className="text-xs text-ink">
      <span className="font-bold">Insights:</span>{' '}
      {!feature.enabled ? (
        <span className="text-ink-mid">not licensed ({feature.reason.replace(/_/g, ' ')})</span>
      ) : counted === 0 ? (
        <span className="text-ink-mid">nothing counted yet of {data.facilities.length} {data.facilities.length === 1 ? 'facility' : 'facilities'}</span>
      ) : (
        <>
          {counted} of {data.facilities.length} {data.facilities.length === 1 ? 'facility' : 'facilities'} counted through {latest ? monthLabel(latest, 'long') : '—'} · average score {formatHeadline('score', data.totals.figures.score)} · call-outs {formatHeadline('callOutsPer100', data.totals.figures.callOutsPer100)} per 100 shifts
        </>
      )}
      <EndpointHint className="ml-2">GET /admin/clients/{'{clientId}'}/insights/portfolio</EndpointHint>
    </p>
  )
}
